import React from 'react';
import { Box, Chip, Link, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  FieldSessionMap,
  formatFieldSessionDistance,
  formatFieldSessionDuration,
} from '../../components';
import type { FieldSessionSummaryData } from '../../components';
import { formatDateTime } from '../../helpers';
import { useIntl } from 'react-intl';

const mapWrapperStyles = { width: '100%', aspectRatio: '16 / 9' };
const detailGridStyles = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
  },
  gap: { xs: 2.5, md: 4 },
  p: { xs: 2, md: 3 },
  borderRadius: 2,
  bgcolor: 'sunken',
};
const titleStyles = { fontWeight: 600 };

function DetailItem({
  label,
  children,
}: React.PropsWithChildren<{ label: string }>) {
  return (
    <Stack gap={0.5} minWidth={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography component="div">{children}</Typography>
    </Stack>
  );
}

export function FieldSessionOverview({
  session,
}: {
  session: FieldSessionSummaryData;
}) {
  const intl = useIntl();
  const isActive = !session.endedAt;
  const findsLabel = intl.formatMessage(
    {
      id: 'fieldSessions.findCount',
      defaultMessage: '{count, plural, one {# find} other {# finds}}',
    },
    { count: session.findCount }
  );
  const notRecordedLabel = intl.formatMessage({
    id: 'fieldSessions.notRecorded',
    defaultMessage: 'Not recorded',
  });

  return (
    <Stack gap={4} mb={6}>
      <Box sx={mapWrapperStyles}>
        <FieldSessionMap track={session.track} borderRadius={2} />
      </Box>
      <Stack gap={2.5}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h4" component="h1" sx={titleStyles}>
            {session.name ||
              intl.formatMessage({
                id: 'fieldSessions.untitled',
                defaultMessage: 'Untitled field session',
              })}
          </Typography>
          {isActive && (
            <Chip
              size="small"
              color="success"
              label={intl.formatMessage({
                id: 'fieldSessions.active',
                defaultMessage: 'Active',
              })}
            />
          )}
        </Stack>
        {session.note && (
          <Typography color="text.secondary" whiteSpace="pre-wrap">
            {session.note}
          </Typography>
        )}
        <Box sx={detailGridStyles}>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.started',
              defaultMessage: 'Started',
            })}
          >
            {formatDateTime(session.startedAt)}
          </DetailItem>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.ended',
              defaultMessage: 'Ended',
            })}
          >
            {session.endedAt
              ? formatDateTime(session.endedAt)
              : intl.formatMessage({
                  id: 'fieldSessions.activeNow',
                  defaultMessage: 'Active now',
                })}
          </DetailItem>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.site',
              defaultMessage: 'Site',
            })}
          >
            {session.siteId ? (
              <Link component={RouterLink} to={`/s/${session.siteId}`}>
                {session.siteName ||
                  intl.formatMessage({
                    id: 'site.genericName',
                    defaultMessage: 'Site',
                  })}
              </Link>
            ) : (
              intl.formatMessage({
                id: 'fieldSessions.noSiteSelected',
                defaultMessage: 'No site selected',
              })
            )}
          </DetailItem>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.trackLength',
              defaultMessage: 'Track length',
            })}
          >
            {formatFieldSessionDistance(
              session.distanceMeters,
              notRecordedLabel
            )}
          </DetailItem>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.duration',
              defaultMessage: 'Duration',
            })}
          >
            {formatFieldSessionDuration(session.activeDurationSeconds)}
          </DetailItem>
          <DetailItem
            label={intl.formatMessage({
              id: 'fieldSessions.finds',
              defaultMessage: 'Finds',
            })}
          >
            {findsLabel}
          </DetailItem>
        </Box>
      </Stack>
    </Stack>
  );
}
